import { combineReducers } from 'redux'
import { Reducer, Collection } from 'interfaces'
import * as groupBy from 'lodash/groupBy'
import * as isNumber from 'lodash/isNumber'
import * as mapValues from 'lodash/mapValues'
import { InsightIdsByPlacement, InsightsAppState, InsightsOrderState, Insight } from 'interfaces/insights'
import { InsightsPageType } from 'interfaces/pages'
import { PAGE_CHANGED, SET_FILTER_CATEGORY, SET_TIMEFRAME } from 'actions/globalActions'
import { SELECT_RELATION, SET_RANDOM_RELATION } from 'actions/peergroupActions'
import { CHANGE_TRANSACTION_CATEGORY, DELETE_MONEYFLOW_TRACKER } from 'actions/moneyflowActions'
import {
  FAIL_TOGGLE_DISMISS_INSIGHT,
  INSIGHT_CTA_CLICK,
  RESET_INSIGHTS,
  SEE_NEXT_INSIGHT,
  SET_ACTIVE_INSIGHT,
  SET_INSIGHTS,
  TOGGLE_DISMISS_INSIGHT,
  UPDATE_PLACEMENT_INSIGHTS,
} from 'actions/insightsActions'
import { createInsightIdsSorter, dismissInsight, undismissInsight } from 'helpers/insightHelpers'
import { getAnchorPage, isInsightsPage } from 'helpers/pageHelpers'
import { URL_PAGE_MAP } from 'constants/pages'
import { isSuccessAction } from 'helpers/actionsHelpers'
import { SORT_OPPORTUNITIES } from 'actions/opportunityActions'
import { OPPORTUNITIES_PAGE_PLACEMENT } from 'constants/opportunities'
import { SocialInnerInsight } from 'interfaces/socialInsights'
import { GET_SOCIAL_INSIGHTS } from 'actions/socialInsightsActions'

const firstInsightsLoadingReducer: Reducer<boolean> = (prevState = true, action) => {
  switch (action.type) {
    case PAGE_CHANGED:
      return isInsightsPage(URL_PAGE_MAP[action.payload.next])

    case SET_INSIGHTS:
      return action.payload.isCurrentPageInsights ? false : prevState

    default:
      return prevState
  }
}

const ORDER_INITIAL_STATE: InsightsOrderState = {
  expectedSuccessor: null,
  byPlacement: {},
  priorRelationId: null,
}

const proccessInsightsPayload = (
  insights: Collection<Insight, 'id'> | Collection<SocialInnerInsight, 'id'>, prevState: InsightsOrderState
) => {
  const { byKey, allKeys } = insights
  const notDismissedKeys = allKeys.filter(
    (id) => !byKey[id].is_dismissed || byKey[id].placement === OPPORTUNITIES_PAGE_PLACEMENT
  )
  const unordered: InsightIdsByPlacement = groupBy(notDismissedKeys, (id) => byKey[id].placement)
  const { priorRelationId, expectedSuccessor } = prevState

  const sorter = createInsightIdsSorter(
    insights,
    expectedSuccessor,
    priorRelationId,
  )

  return {
    ...prevState,
    expectedSuccessor: null,
    byPlacement: {
      ...prevState.byPlacement,
      ...mapValues(unordered, sorter),
    },
    priorRelationId: null,
  }
}

export const orderReducer: Reducer<InsightsOrderState> = (prevState = ORDER_INITIAL_STATE, action) => {
  switch (action.type) {
    case INSIGHT_CTA_CLICK: {
      if (action.payload.type === 'link') {
        const { successor, target } = action.payload.cta
        const expectedSuccessor = successor
          ? {
            ...successor,
            page: getAnchorPage(target) as InsightsPageType,
          }
          : null

        return {
          ...prevState,
          expectedSuccessor,
        }
      }

      return prevState
    }

    case PAGE_CHANGED: {
      if (!prevState.expectedSuccessor || !action.payload.prev) {
        return {
          ...prevState,
          priorRelationId: null,
        }
      }

      const prevSuccessor = prevState.expectedSuccessor
      const url = action.payload.next
      const page = URL_PAGE_MAP[url] || null
      const nextSuccessor = page === prevSuccessor.page ? prevSuccessor : null

      return {
        ...prevState,
        expectedSuccessor: nextSuccessor,
        priorRelationId: null,
      }
    }

    case SET_TIMEFRAME:
    case DELETE_MONEYFLOW_TRACKER:
      return {
        ...prevState,
        priorRelationId: null,
      }

    case RESET_INSIGHTS:
      return {
        ...prevState,
        priorRelationId: action.payload.priorRelationId,
        expectedSuccessor: null,
      }

    case CHANGE_TRANSACTION_CATEGORY: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      return {
        ...prevState,
        priorRelationId: null,
        expectedSuccessor: null,
      }
    }

    case SET_FILTER_CATEGORY:
      return {
        ...prevState,
        priorRelationId: null,
        expectedSuccessor: null,
      }

    case SELECT_RELATION:
      return {
        ...prevState,
        priorRelationId: action.payload,
      }

    case SET_RANDOM_RELATION:
      return {
        ...prevState,
        priorRelationId: action.payload.id,
      }

    case SET_INSIGHTS:
      return proccessInsightsPayload(action.payload.insights, prevState)

    case SORT_OPPORTUNITIES:
      return proccessInsightsPayload(action.payload, prevState)

    case GET_SOCIAL_INSIGHTS:
      if (!isSuccessAction(action)) {
        return prevState
      }

      return proccessInsightsPayload(action.payload.response.insights, prevState)

    case UPDATE_PLACEMENT_INSIGHTS: {
      const { insights, placement } = action.payload
      const notDismissedKeys = insights.allKeys.filter((id) => !insights.byKey[id].is_dismissed)
      const { priorRelationId, expectedSuccessor } = prevState
      const nextAllIds = createInsightIdsSorter(insights, expectedSuccessor, priorRelationId)(notDismissedKeys)

      return {
        ...prevState,
        expectedSuccessor: null,
        priorRelationId: null,
        byPlacement: {
          ...prevState.byPlacement,
          [placement]: nextAllIds,
        },
      }
    }

    case TOGGLE_DISMISS_INSIGHT: {
      const { insight, is_dismissed } = action.payload

      if (is_dismissed) {
        return dismissInsight(insight, prevState)
      }

      return undismissInsight(insight, prevState)
    }

    case FAIL_TOGGLE_DISMISS_INSIGHT: {
      const { insight, is_dismissed } = action.payload

      if (is_dismissed) {
        return undismissInsight(insight, prevState)
      }

      return dismissInsight(insight, prevState)
    }

    case SEE_NEXT_INSIGHT: {
      const placement = action.payload.placement
      const placementIds = prevState.byPlacement[placement]
      if (!placementIds) {
        return prevState
      }

      const ids = [...placementIds]
      const skippedId = ids.shift()
      if (isNumber(skippedId)) {
        ids.push(skippedId)
      }

      const nextByPlacement = {
        ...prevState.byPlacement,
        [placement]: ids,
      }

      return {
        ...prevState,
        byPlacement: nextByPlacement,
      }
    }

    case SET_ACTIVE_INSIGHT: {
      const { id, placement } = action.payload
      const placementIds = prevState.byPlacement[placement]
      if (!placementIds) {
        return prevState
      }

      const start = placementIds.indexOf(id)
      const ids = placementIds.slice(start).concat(placementIds.slice(0, start))

      const nextByPlacement = {
        ...prevState.byPlacement,
        [placement]: ids,
      }

      return {
        ...prevState,
        byPlacement: nextByPlacement,
      }
    }

    default:
      return prevState
  }
}

export default combineReducers<InsightsAppState>({
  firstInsightsLoading: firstInsightsLoadingReducer,
  order: orderReducer,
})
